import React, { useEffect, useState } from "react";
import axios from "axios";
import { crmHeaders } from "../utils/crmApi";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const emptyAccessForCountries = (countries) =>
    countries.map((c) => ({
        country: c.Country,
        leads: false,
        emailIds: [],
    }));

export function mergeAccessWithCountries(countries, existingAccess) {
    const map = new Map((existingAccess || []).map((a) => [a.country, a]));
    return countries.map((c) => {
        const saved = map.get(c.Country);
        const emailIds = Array.isArray(saved?.emailIds)
            ? saved.emailIds.map((id) => parseInt(id, 10)).filter(Boolean)
            : [];
        return {
            country: c.Country,
            leads: !!saved?.leads,
            emailIds,
        };
    });
}

export default function CountryAccessPicker({ access, onChange, disabled }) {
    const [countries, setCountries] = useState([]);
    const [emailsByCountry, setEmailsByCountry] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            axios.get(`${BASE_URL}/getcountries`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/getemails`, { headers: crmHeaders() }),
        ])
            .then(([countriesRes, emailsRes]) => {
                const active = countriesRes.data.filter((c) => c.Status === "Active");
                setCountries(active);

                const grouped = {};
                (emailsRes.data || []).forEach((acc) => {
                    const key = acc.Country || "Other";
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(acc);
                });
                setEmailsByCountry(grouped);

                if (!access?.length) {
                    onChange(emptyAccessForCountries(active));
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const toggleLeads = (country) => {
        if (disabled) return;
        onChange(
            (access || []).map((row) =>
                row.country === country ? { ...row, leads: !row.leads } : row
            )
        );
    };

    const toggleEmail = (country, emailId) => {
        if (disabled) return;
        onChange(
            (access || []).map((row) => {
                if (row.country !== country) return row;
                const ids = new Set(row.emailIds || []);
                if (ids.has(emailId)) ids.delete(emailId);
                else ids.add(emailId);
                return { ...row, emailIds: [...ids] };
            })
        );
    };

    const selectAllEmails = (country, emailList) => {
        if (disabled || !emailList?.length) return;
        const allIds = emailList.map((e) => e.Id);
        onChange(
            (access || []).map((row) =>
                row.country === country ? { ...row, emailIds: allIds } : row
            )
        );
    };

    if (loading) return <p className="text-muted">Loading countries and emails...</p>;
    if (!countries.length) return <p className="text-warning">No active countries found.</p>;

    const rows = mergeAccessWithCountries(countries, access);

    return (
        <div>
            <p className="text-muted mb-3">
                Har country ke liye <strong>Leads</strong> aur kaun si <strong>company emails</strong> is
                user ko milengi (send + receive dono) select karein.
            </p>
            {rows.map((row) => {
                const countryEmails = emailsByCountry[row.country] || [];
                return (
                    <div key={row.country} className="card bg-dark border-secondary mb-3">
                        <div className="card-header d-flex align-items-center justify-content-between py-2">
                            <strong className="text-light">{row.country}</strong>
                            <label className="form-check-label text-light mb-0">
                                <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={!!row.leads}
                                    onChange={() => toggleLeads(row.country)}
                                    disabled={disabled}
                                />
                                Leads access
                            </label>
                        </div>
                        <div className="card-body py-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted">Email accounts (send &amp; inbox)</small>
                                {countryEmails.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm text-info p-0"
                                        onClick={() => selectAllEmails(row.country, countryEmails)}
                                        disabled={disabled}
                                    >
                                        Select all
                                    </button>
                                )}
                            </div>
                            {countryEmails.length === 0 ? (
                                <small className="text-warning">
                                    Is country ke liye koi email add nahi — pehle Emails menu se add karein.
                                </small>
                            ) : (
                                <div className="row">
                                    {countryEmails.map((acc) => (
                                        <div key={acc.Id} className="col-md-6 col-lg-4 mb-1">
                                            <label className="form-check-label text-light small">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-2"
                                                    checked={(row.emailIds || []).includes(acc.Id)}
                                                    onChange={() => toggleEmail(row.country, acc.Id)}
                                                    disabled={disabled}
                                                />
                                                {acc.Email}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
